import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import QuoteRequest from "./pages/QuoteRequest";
import Profile from "./pages/Profile";
import Beneficiaries from "./pages/Beneficiaries";
import FileClaim from "./pages/FileClaim";
import ClaimDetails from "./pages/ClaimDetails";
import Documents from "./pages/Documents";
import Referrals from "./pages/Referrals";

// Agent Pages
import AgentLogin from "./pages/agent/AgentLogin";
import AgentDashboard from "./pages/agent/AgentDashboard";
import AgentAnalytics from "./pages/agent/AgentAnalytics";
import AgentQuotes from "./pages/agent/AgentQuotes";
import AgentClaims from "./pages/agent/AgentClaims";
import AgentClaimDetails from "./pages/agent/AgentClaimDetails";
import AgentCustomers from "./pages/agent/AgentCustomers";
import AgentCustomerDetails from "./pages/agent/AgentCustomerDetails";
import AgentPayments from "./pages/agent/AgentPayments";
import AgentTeam from "./pages/agent/AgentTeam";
import AgentAuditLogs from "./pages/agent/AgentAuditLogs";
import AgentSettings from "./pages/agent/AgentSettings";
import SystemHealth from "./pages/agent/SystemHealth";
import AgentWithdrawals from "./pages/agent/AgentWithdrawals";
import AgentPolicyContracts from "./pages/agent/AgentPolicyContracts";
import AgentESignatures from "./pages/agent/AgentESignatures";
import AgentSupportTickets from "./pages/agent/AgentSupportTickets";
import AgentKnowledgeBase from "./pages/agent/AgentKnowledgeBase";
import AgentReferrals from "./pages/agent/AgentReferrals";
import AgentMyCommissions from "./pages/agent/AgentMyCommissions";
import AgentTeamStatus from "./pages/agent/AgentTeamStatus";

// User Support Pages
import FAQPage from "./pages/FAQPage";
import SupportPage from "./pages/SupportPage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";

// Static Pages
import AboutPage from "./pages/AboutPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import CareersPage from "./pages/CareersPage";

// Agent Careers Page
import AgentCareers from "./pages/agent/AgentCareers";

// HR Module
import AgentHR from "./pages/agent/AgentHR";
import EmployeeSelfService from "./pages/agent/EmployeeSelfService";

// Organization & Communication
import OrganizationChart from "./pages/agent/OrganizationChart";
import Channels from "./pages/agent/Channels";

// Marketing & Payments
import AgentNewsletters from "./pages/agent/AgentNewsletters";
import AgentPromoCodes from "./pages/agent/AgentPromoCodes";
import AgentStripeDashboard from "./pages/agent/AgentStripeDashboard";
import AgentMarketing from "./pages/agent/AgentMarketing";
import FlyerGenerator from "./pages/agent/FlyerGenerator";
import VisitorInsights from "./pages/agent/VisitorInsights";

// Components
import { Toaster } from "./components/ui/sonner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import AIChatWidget from "./components/AIChatWidget";
import CookieConsent from "./components/CookieConsent";
import InactivityTracker from "./components/InactivityTracker";
import { PreferencesProvider } from "./components/Preferences";
import AnalyticsTracker from "./components/AnalyticsTracker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Agent Protected Route
const AgentProtectedRoute = ({ children }) => {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("agent_token");

  const handleAgentLogout = () => {
    localStorage.removeItem("agent_token");
    navigate("/agent/login");
  };

  useEffect(() => {
    if (!token) {
      navigate("/agent/login");
      return;
    }

    axios.get(`${API}/agent/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      setAgent(res.data);
      setLoading(false);
    })
    .catch(() => {
      localStorage.removeItem("agent_token");
      navigate("/agent/login");
    });
  }, [navigate, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <InactivityTracker 
        userType="agent" 
        token={token} 
        onLogout={handleAgentLogout}
      />
      {children}
    </>
  );
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* User Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/quote" element={<ProtectedRoute><QuoteRequest /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/beneficiaries" element={<ProtectedRoute><Beneficiaries /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
      <Route path="/file-claim" element={<ProtectedRoute><FileClaim /></ProtectedRoute>} />
      <Route path="/claims/:claimId" element={<ProtectedRoute><ClaimDetails /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      
      {/* Public Pages */}
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
      <Route path="/knowledge-base/:articleId" element={<KnowledgeBasePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/contact" element={<Navigate to="/support" replace />} />
      
      {/* Agent Routes */}
      <Route path="/agent/login" element={<AgentLogin />} />
      <Route path="/agent/dashboard" element={<AgentProtectedRoute><AgentDashboard /></AgentProtectedRoute>} />
      <Route path="/agent/analytics" element={<AgentProtectedRoute><AgentAnalytics /></AgentProtectedRoute>} />
      <Route path="/agent/quotes" element={<AgentProtectedRoute><AgentQuotes /></AgentProtectedRoute>} />
      <Route path="/agent/claims" element={<AgentProtectedRoute><AgentClaims /></AgentProtectedRoute>} />
      <Route path="/agent/claims/:claimId" element={<AgentProtectedRoute><AgentClaimDetails /></AgentProtectedRoute>} />
      <Route path="/agent/customers" element={<AgentProtectedRoute><AgentCustomers /></AgentProtectedRoute>} />
      <Route path="/agent/customers/:userId" element={<AgentProtectedRoute><AgentCustomerDetails /></AgentProtectedRoute>} />
      <Route path="/agent/payments" element={<AgentProtectedRoute><AgentPayments /></AgentProtectedRoute>} />
      <Route path="/agent/team" element={<AgentProtectedRoute><AgentTeam /></AgentProtectedRoute>} />
      <Route path="/agent/audit-logs" element={<AgentProtectedRoute><AgentAuditLogs /></AgentProtectedRoute>} />
      <Route path="/agent/system-health" element={<AgentProtectedRoute><SystemHealth /></AgentProtectedRoute>} />
      <Route path="/agent/settings" element={<AgentProtectedRoute><AgentSettings /></AgentProtectedRoute>} />
      <Route path="/agent/withdrawals" element={<AgentProtectedRoute><AgentWithdrawals /></AgentProtectedRoute>} />
      <Route path="/agent/contracts" element={<AgentProtectedRoute><AgentPolicyContracts /></AgentProtectedRoute>} />
      <Route path="/agent/e-signatures" element={<AgentProtectedRoute><AgentESignatures /></AgentProtectedRoute>} />
      <Route path="/agent/support-tickets" element={<AgentProtectedRoute><AgentSupportTickets /></AgentProtectedRoute>} />
      <Route path="/agent/knowledge-base" element={<AgentProtectedRoute><AgentKnowledgeBase /></AgentProtectedRoute>} />
      <Route path="/agent/referrals" element={<AgentProtectedRoute><AgentReferrals /></AgentProtectedRoute>} />
      <Route path="/agent/my-commissions" element={<AgentProtectedRoute><AgentMyCommissions /></AgentProtectedRoute>} />
      <Route path="/agent/team-status" element={<AgentProtectedRoute><AgentTeamStatus /></AgentProtectedRoute>} />
      <Route path="/agent/careers" element={<AgentProtectedRoute><AgentCareers /></AgentProtectedRoute>} />
      <Route path="/agent/hr" element={<AgentProtectedRoute><AgentHR /></AgentProtectedRoute>} />
      <Route path="/agent/self-service" element={<AgentProtectedRoute><EmployeeSelfService /></AgentProtectedRoute>} />
      <Route path="/agent/org-chart" element={<AgentProtectedRoute><OrganizationChart /></AgentProtectedRoute>} />
      <Route path="/agent/channels" element={<AgentProtectedRoute><Channels /></AgentProtectedRoute>} />
      <Route path="/agent/newsletters" element={<AgentProtectedRoute><AgentNewsletters /></AgentProtectedRoute>} />
      <Route path="/agent/promo-codes" element={<AgentProtectedRoute><AgentPromoCodes /></AgentProtectedRoute>} />
      <Route path="/agent/stripe-dashboard" element={<AgentProtectedRoute><AgentStripeDashboard /></AgentProtectedRoute>} />
      <Route path="/agent/marketing" element={<AgentProtectedRoute><AgentMarketing /></AgentProtectedRoute>} />
      <Route path="/agent/flyer-generator" element={<AgentProtectedRoute><FlyerGenerator /></AgentProtectedRoute>} />
      <Route path="/agent/visitor-insights" element={<AgentProtectedRoute><VisitorInsights /></AgentProtectedRoute>} />
    </Routes>
  );
}

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        setUser(res.data);
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    axios.post(`${API}/auth/logout`).catch(() => {});
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, setUser }}>
      {children}
      {/* Inactivity tracker for regular users */}
      {token && user && (
        <InactivityTracker 
          userType="user" 
          token={token} 
          onLogout={logout}
        />
      )}
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <PreferencesProvider>
        <AuthProvider>
          <AnalyticsTracker />
          <AppRouter />
          <Toaster position="top-right" richColors />
          <PWAInstallPrompt />
          <AIChatWidget />
          <CookieConsent />
        </AuthProvider>
      </PreferencesProvider>
    </BrowserRouter>
  );
}

export default App;
